import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Lock, Save, Plus, Trash2, Calendar, Clock, Globe,
  MapPin, ExternalLink, Eye, Music, Ticket, Image, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const VenueDashboard = () => {
  const [token, setToken] = useState(() => localStorage.getItem('venue_token') || '');
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', access_code: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (token) fetchVenueData();
  }, [token]);

  const fetchVenueData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/venue-dashboard/me`, { headers: { Authorization: `Bearer ${token}` } });
      setVenue(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('venue_token');
        setToken('');
        toast.error('Session expired. Please log in again.');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/venue-dashboard/login`, loginForm);
      const t = res.data.token;
      localStorage.setItem('venue_token', t);
      setToken(t);
      toast.success(`Welcome, ${res.data.venue.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoginLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('venue_token');
    setToken('');
    setVenue(null);
  };

  if (!token) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="bg-card border-border">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center mb-3">
                <Building2 size={32} className="text-purple-400" />
              </div>
              <CardTitle className="text-2xl">Venue Dashboard</CardTitle>
              <CardDescription>Log in with your venue email and access code</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={loginForm.email}
                    onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="venue@email.com"
                    required
                    data-testid="venue-login-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Access Code</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      value={loginForm.access_code}
                      onChange={e => setLoginForm(p => ({ ...p, access_code: e.target.value }))}
                      placeholder="Your access code"
                      className="pl-10"
                      required
                      data-testid="venue-login-code"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground">Your access code was provided when your venue was approved</p>
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading} data-testid="venue-login-btn">
                  {loginLoading ? 'Logging in...' : 'Log In'}
                </Button>
              </form>
              <p className="text-xs text-center text-muted-foreground mt-4">
                Don't have an account? <Link to="/register/venue" className="text-purple-400 hover:underline">Register your venue</Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (loading && !venue) {
    return <div className="text-center py-20"><RefreshCw size={24} className="animate-spin mx-auto text-purple-400" /></div>;
  }

  if (!venue) return null;

  const { venue_page, events, stats } = venue;
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'edit', label: 'Edit Page' },
    { key: 'events', label: 'Events' },
  ];

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="venue-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{venue_page.name}</h1>
          <p className="text-sm text-muted-foreground">Venue Dashboard</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/venue/${stats.page_slug}`}>
            <Button size="sm" variant="outline" className="text-xs"><Eye size={13} className="mr-1" /> View Page</Button>
          </Link>
          <Button size="sm" variant="ghost" onClick={handleLogout} className="text-xs text-gray-500">Logout</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === t.key ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300 bg-card border border-border'}`}
            data-testid={`venue-tab-${t.key}`}
          >
            {t.label}
            {t.key === 'events' && <span className="ml-1 text-xs opacity-60">{events?.length || 0}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab venue={venue} />}
      {activeTab === 'edit' && <EditTab token={token} venuePage={venue_page} onSave={fetchVenueData} />}
      {activeTab === 'events' && <EventsTab token={token} venuePage={venue_page} events={events} onRefresh={fetchVenueData} />}
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ venue }) => {
  const { venue_page, events, stats } = venue;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Upcoming Events" value={stats.upcoming_events} />
        <StatCard label="Page Slug" value={`/venue/${stats.page_slug}`} />
        <StatCard label="Website" value={venue_page.website ? 'Linked' : 'Not set'} />
        <StatCard label="Cover Image" value={venue_page.cover_image ? 'Set' : 'Missing'} />
      </div>

      {venue_page.cover_image && (
        <Card className="bg-card border-border overflow-hidden">
          <img src={venue_page.cover_image} alt="Cover" className="w-full h-40 object-cover" />
        </Card>
      )}

      <Card className="bg-card border-border">
        <CardContent className="pt-4">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</h3>
          <p className="text-sm text-gray-300">{venue_page.description || 'No description set'}</p>
        </CardContent>
      </Card>

      {events && events.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Next Events</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {events.slice(0, 5).map(e => (
              <div key={e.id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                <div>
                  <p className="text-sm font-semibold">{e.title}</p>
                  <p className="text-xs text-gray-500">{e.date} at {e.time}</p>
                </div>
                {e.ticket_url && <a href={e.ticket_url} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400"><Ticket size={12} /></a>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="p-3 rounded-xl border border-border bg-card">
    <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm font-semibold mt-0.5 truncate">{value}</p>
  </div>
);

// Edit Tab
const EditTab = ({ token, venuePage, onSave }) => {
  const [form, setForm] = useState({
    description: venuePage.description || '',
    cover_image: venuePage.cover_image || '',
    vibe: venuePage.vibe || '',
    genre_focus: venuePage.genre_focus || '',
    website: venuePage.website || '',
    events_url: venuePage.events_url || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/venue-dashboard/me`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Page updated!');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-sm">Edit Your Landing Page</CardTitle>
          <CardDescription>Changes appear on your public venue page immediately</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Description</Label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={4} placeholder="Tell visitors about your venue..." data-testid="edit-venue-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Cover Image URL</Label>
              <Input value={form.cover_image} onChange={e => setForm(p => ({ ...p, cover_image: e.target.value }))} placeholder="https://..." data-testid="edit-venue-cover" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Vibe / Atmosphere</Label>
              <Input value={form.vibe} onChange={e => setForm(p => ({ ...p, vibe: e.target.value }))} placeholder="e.g., Casual bar with live music" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Genres</Label>
              <Input value={form.genre_focus} onChange={e => setForm(p => ({ ...p, genre_focus: e.target.value }))} placeholder="Rock, Jazz, Hip Hop..." />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Website</Label>
              <Input value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://yourvenue.com" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-500">Events/Tickets URL</Label>
              <Input value={form.events_url} onChange={e => setForm(p => ({ ...p, events_url: e.target.value }))} placeholder="https://yourvenue.com/events" />
            </div>
          </div>
          {form.cover_image && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Cover Preview</p>
              <img src={form.cover_image} alt="Preview" className="w-full h-32 object-cover rounded-lg border border-border" />
            </div>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="save-venue-page-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Events Tab
const EventsTab = ({ token, venuePage, events, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', time: '', description: '', genre: '', ticket_url: '', image_url: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/venue-dashboard/events`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event added!');
      setForm({ title: '', date: '', time: '', description: '', genre: '', ticket_url: '', image_url: '' });
      setShowForm(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add event');
    }
    setSubmitting(false);
  };

  const handleDelete = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/venue-dashboard/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event removed');
      onRefresh();
    } catch {
      toast.error('Failed to delete — you can only delete events you submitted');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Your Events</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-event-btn">
          <Plus size={14} className="mr-1" /> Add Event
        </Button>
      </div>

      {/* Add Event Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
          <Card className="bg-card border-border">
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Event Title *</Label>
                    <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required placeholder="Event name" data-testid="event-title-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Genre</Label>
                    <Input value={form.genre} onChange={e => setForm(p => ({ ...p, genre: e.target.value }))} placeholder="Rock, Jazz, etc." />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Date *</Label>
                    <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required data-testid="event-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Time *</Label>
                    <Input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required data-testid="event-time-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Description</Label>
                  <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Tell people about this event..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Ticket URL</Label>
                    <Input value={form.ticket_url} onChange={e => setForm(p => ({ ...p, ticket_url: e.target.value }))} placeholder="https://..." />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Event Image URL</Label>
                    <Input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting} data-testid="submit-event-btn">
                    {submitting ? 'Adding...' : 'Add Event'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Events List */}
      {events && events.length > 0 ? (
        <div className="space-y-2">
          {events.map(event => (
            <div key={event.id} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card" data-testid={`venue-dash-event-${event.id}`}>
              {event.image_url && <img src={event.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{event.title}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Calendar size={10} /> {event.date}</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {event.time}</span>
                  {event.genre && <span className="text-purple-400">{event.genre}</span>}
                </div>
              </div>
              {event.submitted_by && (
                <button onClick={() => handleDelete(event.id)} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition" title="Delete">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 rounded-xl border border-border bg-card/30">
          <Calendar size={36} className="mx-auto mb-2 text-gray-600" />
          <p className="text-gray-500 text-sm">No upcoming events</p>
          <p className="text-xs text-gray-600 mt-1">Add events to show them on your venue page</p>
        </div>
      )}
    </div>
  );
};

export default VenueDashboard;
